import classNames from 'classnames';
import * as React from 'react';
import type { ReactNode } from 'react';
import buttonStyles from '../Button/Button.module.css';
import { ButtonSize } from '../Button/ButtonSize.csharp';
import { ButtonTier } from '../Button/ButtonTier.csharp';
import { ButtonType } from '../Button/ButtonType.csharp';
import { SvgIcon } from '../SvgIcon/SvgIcon';
import { useTheme } from '~/Features/Layouts/Theme/Context/ThemeContextProvider';
import type { StrictOmit } from '~/Partials/Utils/typeUtils';
import { omitProperty } from '~/Partials/Utils/utils';
import type { Theme } from '~/Utils/Theme';
import styles from './Link.module.css';
import { LinkType } from './LinkType.csharp';
import { LinkVariant } from './LinkVariant.csharp';
import type { StyledLinkViewModel } from './StyledLinkViewModel.csharp';

export interface BaseLinkProps extends StrictOmit<StyledLinkViewModel, 'label'> {
    theme?: Theme;
    labelClassName?: string;
}

interface LinkWithLabel extends BaseLinkProps {
    label: string;
}

interface LinkWithChildren extends BaseLinkProps {
    children: ReactNode;
}

type NativeLinkAttributes = Pick<
    React.AnchorHTMLAttributes<HTMLAnchorElement>,
    | 'aria-selected'
    | 'aria-controls'
    | 'aria-expanded'
    | 'role'
    | 'aria-haspopup'
    | 'aria-describedby'
    | 'aria-label'
    | 'tabIndex'
    | 'onClick'
    | 'lang'
    | 'download'
    | 'title'
>;

export type LinkProps = (LinkWithChildren | LinkWithLabel) & NativeLinkAttributes;

export const Link = React.forwardRef<HTMLAnchorElement, React.PropsWithChildren<LinkProps>>((props, ref) => {
    const { children, theme, ...passThroughProps } = props;
    const { type, buttonType } = passThroughProps;

    const filteredProps = 'label' in passThroughProps ? omitProperty(passThroughProps, 'label') : passThroughProps;

    const elementToRender = children ?? ('label' in props && props.label);

    if (type === LinkType.Button && buttonType !== ButtonType.Clear) {
        return <ThemedButtonLink {...filteredProps} theme={theme} elementToRender={elementToRender} ref={ref} />;
    }

    return <RawLink {...filteredProps} elementToRender={elementToRender} ref={ref} />;
});

interface RawLinkProps extends StrictOmit<BaseLinkProps, 'theme'>, NativeLinkAttributes {
    elementToRender: ReactNode;
}

const RawLink = React.forwardRef<HTMLAnchorElement, RawLinkProps>((props, ref) => {
    const {
        elementToRender,
        className,
        labelClassName,
        type = LinkType.Default,
        variant = LinkVariant.Default,
        buttonType = ButtonType.Solid,
        buttonTier = ButtonTier.LegacyPrimary,
        buttonSize = ButtonSize.Large,
        external = false,
        showIsExternal = true,
        ...additionalProps
    } = props;

    const getLinkClassName = () => {
        if (type === LinkType.Button) {
            return classNames(
                styles.Link,
                styles['Variant--button'],
                buttonStyles[`Variant--${buttonType}`],
                buttonStyles[`Variant--${buttonTier}`],
                buttonStyles[`Variant--${buttonSize}`],
                className
            );
        }

        return classNames(
            styles.Link,
            styles[`Variant--${type}`],
            variant !== 'default' && styles[`Variant--${variant}Variant`],
            className
        );
    };

    return (
        <a
            {...additionalProps}
            className={getLinkClassName()}
            {...(external ? { target: '_blank', rel: 'noopener noreferrer' } : {})}
            ref={ref}
            data-owned="" // In order to differentiate links e.g. inside Richtext
        >
            <div className={classNames(styles.Label, labelClassName)}>{elementToRender}</div>
            {external && showIsExternal && <SvgIcon iconName="external-link" className={styles.ExternalIcon} />}
        </a>
    );
});

interface ThemedButtonLinkProps extends RawLinkProps {
    theme?: Theme;
}

const ThemedButtonLink = React.forwardRef<HTMLAnchorElement, ThemedButtonLinkProps>((props, ref) => {
    const contextualTheme = useTheme();
    const { className, theme = contextualTheme, ...additionalProps } = props;

    const { withBoxShadow, withRoundedCorners } = theme;

    return (
        <RawLink
            {...additionalProps}
            className={classNames(
                withRoundedCorners && buttonStyles['Variant--rounded'],
                withBoxShadow && buttonStyles['Variant--shadowed'],
                className
            )}
            ref={ref}
        />
    );
});
